import React, { useState, useContext, useEffect } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { SketchField, Tools } from 'react-sketch';
import IconButton from '@material-ui/core/IconButton';
import { ToolBarContext } from '../../App';
import Tooltip from '@material-ui/core/Tooltip';
import Collapse from '@material-ui/core/Collapse';
import { CompactPicker } from 'react-color';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContentText from '@material-ui/core/DialogContentText';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { MeetingContext } from '../../App';
import { fabric } from 'fabric';
import _ from 'lodash';
import useMainSpeaker from '../../hooks/useMainSpeaker/useMainSpeaker';
import Mousetrap from 'mousetrap';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '55px',
      height: '440px',
      backgroundColor: '#212121',
      position: 'fixed',
      top: '120px',
      right: 0,
      zIndex: 1000,
    },
    toolbarButton: {
      height: '23px',
      width: '23px',
    },
    activeIcon: {
      background: '#03A9F4',
      '&:hover': {
        backgroundColor: '#03A9F4',
      },
    },
    colorPicker: {
      position: 'absolute',
      right: '55px',
      bottom: '23px',
    },
    sketchArea: {
      position: 'absolute',
      top: '0',
      zIndex: 1000,
      width: '10px',
      height: '10px',
    },
  })
);

export default function SketchToolBar() {
  const classes = useStyles();
  const { toolbarEnabled, setSketchTouched } = useContext(ToolBarContext);
  const [tool, setTool] = useState(null);
  const [activeIcon, setActiveIcon] = useState('Select');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [expandColors, setExpandColors] = useState(false);
  const [lineColor, setLineColor] = useState('red');
  const [textActive, setTextActive] = useState(false);
  const [text, setText] = useState('');
  const [sketchJSON, setsketchJSON] = useState({});
  const {
    sendDataToParticipants,
    recievedCanvasData,
    currentUserOnSketchField,
    hostCanvasHeight,
    hostCanvasWidth,
    isSketchActive,
  } = useVideoContext();
  const { userType } = useContext(MeetingContext);
  const sketch = React.useRef(null);
  const mainParticipant = useMainSpeaker();
  const intialCanvasSize = 10;
  const [canvasHeight, setCanvasHeight] = useState(intialCanvasSize);
  const [canvasWidth, setCanvaswidth] = useState(intialCanvasSize);

  const resizeObject = (object, sX, sY) => {
    object.scaleX = object.scaleX * sX;
    object.scaleY = object.scaleY * sY;
    object.left = object.left * sX;
    object.top = object.top * sY;
    object.setCoords();
  };

  const setCanvasDimensions = (video: HTMLVideoElement) => {
    let aspectRatioDiff;
    aspectRatioDiff = (video.videoWidth / video.videoHeight) * (video.offsetHeight / video.offsetWidth);
    if (aspectRatioDiff && aspectRatioDiff > 1) {
      setCanvaswidth(video.offsetWidth);
      setCanvasHeight(video.offsetHeight / aspectRatioDiff);
    } else if (aspectRatioDiff) {
      setCanvasHeight(video.offsetHeight);
      setCanvaswidth(video.offsetWidth * aspectRatioDiff);
    }
  };

  //useEffect for setting canvas width and height
  useEffect(() => {
    const timer = setTimeout(() => {
      const mainVideoContainer = document.getElementById('main-container');
      const video = mainVideoContainer.getElementsByTagName('video')[0];
      if (video) {
        setCanvasDimensions(video);
      }
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  });

  //useEffect for rendering canvas on guest side
  useEffect(() => {
    let timer;
    if (userType === 'guest' && canvasWidth !== intialCanvasSize) {
      const sketchdiv = document.getElementById('sketchArea');
      if (currentUserOnSketchField !== null && mainParticipant.sid === currentUserOnSketchField.sid && isSketchActive) {
        timer = setTimeout(() => {
          const mainVideoContainer = document.getElementById('main-container');
          const video = mainVideoContainer.getElementsByTagName('video')[0];
          sketchdiv.innerHTML = '';
          if (video) {
            const sketchdiv = document.getElementById('sketchArea');
            sketchdiv.innerHTML = '';
            const guestCanvas = document.createElement('canvas');
            guestCanvas.id = 'guestCanvas';
            document.getElementById('sketchArea').appendChild(guestCanvas);
            setCanvasDimensions(video);
            var canvas = new fabric.Canvas('guestCanvas', { width: canvasWidth, height: canvasHeight });
            canvas.loadFromJSON(recievedCanvasData, function() {
              canvas.renderAll();
              // // resize the cloned objects
              var scaleMultiplierX = canvasHeight / hostCanvasHeight;
              var scaleMultiplierY = canvasWidth / hostCanvasWidth;
              var objects = canvas.getObjects();
              for (var i in objects) {
                resizeObject(objects[i], scaleMultiplierX, scaleMultiplierY);
              }
              canvas.renderAll();
            });
          }
        }, 500);
      } else sketchdiv.innerHTML = '';
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [recievedCanvasData, mainParticipant, isSketchActive]);

  //useEffect for adding/removing shortcut keys
  useEffect(() => {
    if (toolbarEnabled) {
      setTool('Pencil');
      setActiveIcon('Pencil');
      Mousetrap.bind('ctrl+z', handleRedoMouseTrap);
      Mousetrap.bind('ctrl+shift+z', redo);
      Mousetrap.bind('ctrl+shift+e', clear);
    } else if (userType === 'host') {
      setTool('Select');
      clear();
    }
    return () => {
      if (toolbarEnabled) {
        Mousetrap.unbind('ctrl+z');
        Mousetrap.unbind('ctrl+shift+z');
        Mousetrap.unbind('ctrl+shift+e');
      }
    };
  }, [toolbarEnabled]);

  const undo = () => {
    sketch.current.undo();
    setCanUndo(sketch.current.canUndo());
  };

  const redo = () => {
    sketch.current.redo();
    setCanRedo(sketch.current.canRedo());
    setCanUndo(sketch.current.canUndo());
  };

  const handleRedoMouseTrap = () => {
    if (!(sketch.current.toJSON().objects.length === 0)) undo();
  };

  const handleSketchChange = () => {
    const currentSketchJSON = sketch.current.toJSON();
    if (!_.isEqual(sketchJSON, currentSketchJSON)) {
      sendDataToParticipants(
        JSON.stringify({
          type: 'ANNOTATION_DATA',
          data: {
            sketchJSON: currentSketchJSON,
            canvasHeight: canvasHeight,
            canvasWidth: canvasWidth,
          },
        })
      );
      setsketchJSON(currentSketchJSON);
    }
    if (canUndo !== sketch.current.canUndo()) {
      setCanUndo(sketch.current.canUndo());
      setSketchTouched(!(sketch.current.toJSON().objects.length === 0));
    }
  };

  const clear = () => {
    sketch.current.clear();
    setsketchJSON({});
    sendDataToParticipants(
      JSON.stringify({
        type: 'ANNOTATION_DATA',
        data: {
          sketchJSON: sketchJSON,
          canvasHeight: canvasHeight,
          canvasWidth: canvasWidth,
        },
      })
    );
    setCanRedo(sketch.current.canRedo());
    setCanUndo(sketch.current.canUndo());
    setSketchTouched(!(sketch.current.toJSON().objects.length === 0));
  };

  const addText = () => {
    sketch.current.addText(text, { fill: `${lineColor}`, editable: true });
  };

  return (
    <div
      id="sketchArea"
      style={{ width: canvasWidth, height: canvasHeight, objectFit: 'contain', position: 'absolute' }}
    >
      {userType === 'host' ? (
        <>
          <SketchField
            width={canvasWidth}
            height={canvasHeight}
            tool={Tools[`${tool}`]}
            ref={sketch}
            undoSteps={100}
            lineColor={lineColor}
            lineWidth={3}
            style={{ position: 'absolute', top: '0', zIndex: '1000' }}
            onChange={handleSketchChange}
          />
          <>
            {toolbarEnabled ? (
              <div className={classes.container}>
                <Tooltip title={'Undo ctrl+z'} placement="left">
                  <div>
                    {/* has this div because disabled buttons does not fire any events, so for
                                        adding tooltip div is need */}
                    <IconButton color="secondary" onClick={undo} disabled={!canUndo}>
                      <img
                        src={require('../../assests/images/undo.png')}
                        className={classes.toolbarButton}
                        alt="UndoIcon"
                      ></img>
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip title={'Redo ctrl+shift+z'} placement="left">
                  <IconButton color="secondary" onClick={redo}>
                    <img
                      src={require('../../assests/images/redo.png')}
                      className={classes.toolbarButton}
                      alt="RedoIcon"
                    ></img>
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Circle'} placement="left">
                  <IconButton
                    color="secondary"
                    className={activeIcon === 'Circle' ? classes.activeIcon : ''}
                    onClick={() => {
                      setTool('Circle');
                      setActiveIcon('Circle');
                    }}
                  >
                    <img
                      src={require('../../assests/images/circle.png')}
                      className={classes.toolbarButton}
                      alt="circleIcon"
                    ></img>
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Box'} placement="left">
                  <IconButton
                    color="secondary"
                    className={activeIcon === 'Rectangle' ? classes.activeIcon : ''}
                    onClick={() => {
                      setTool('Rectangle');
                      setActiveIcon('Rectangle');
                    }}
                  >
                    <img
                      src={require('../../assests/images/square.png')}
                      className={classes.toolbarButton}
                      alt="RectangleIcon"
                    ></img>
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Pencil'} placement="left">
                  <IconButton
                    color="secondary"
                    className={activeIcon === 'Pencil' ? classes.activeIcon : ''}
                    onClick={() => {
                      setTool('Pencil');
                      setActiveIcon('Pencil');
                    }}
                  >
                    <img
                      src={require('../../assests/images/pencil.png')}
                      className={classes.toolbarButton}
                      alt="PencilIcon"
                    ></img>
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Select'} placement="left">
                  <IconButton
                    color="secondary"
                    className={activeIcon === 'Select' ? classes.activeIcon : ''}
                    onClick={() => {
                      setTool('Select');
                      setActiveIcon('Select');
                    }}
                  >
                    <img
                      src={require('../../assests/images/select.svg')}
                      className={classes.toolbarButton}
                      alt="SelectIcon"
                    ></img>
                  </IconButton>
                </Tooltip>
                <Tooltip title={'Text'} placement="left">
                  <IconButton
                    color="secondary"
                    onClick={() => {
                      setTextActive(!textActive);
                    }}
                  >
                    <img
                      src={require('../../assests/images/text.svg')}
                      className={classes.toolbarButton}
                      alt="TextIcon"
                    ></img>
                  </IconButton>
                </Tooltip>
                {/* text dialog */}
                {textActive && (
                  <Dialog
                    disableBackdropClick
                    open={textActive}
                    onClose={() => {
                      setTextActive(false);
                    }}
                    fullWidth={true}
                    maxWidth={'xs'}
                  >
                    <DialogTitle id="text-dialog-title">Add Text</DialogTitle>
                    <DialogContent>
                      <DialogContentText>Enter the text below</DialogContentText>
                      <TextField autoFocus fullWidth value={text} onChange={e => setText(e.target.value)} />
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={() => {
                          addText();
                          setTextActive(false);
                          setText('');
                        }}
                        variant="contained"
                      >
                        Add text
                      </Button>
                      <Button
                        onClick={() => {
                          setTextActive(false);
                          setText('');
                        }}
                        variant="contained"
                        color="secondary"
                      >
                        Cancel
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}
                <Tooltip title={'ColorPicker'} placement="left">
                  <IconButton color="secondary" onClick={() => setExpandColors(!expandColors)}>
                    <img
                      src={require('../../assests/images/color.svg')}
                      className={classes.toolbarButton}
                      alt="ColorPickerIcon"
                    ></img>
                  </IconButton>
                </Tooltip>
                {/* collapse for the color picker*/}
                <Collapse in={expandColors} className={classes.colorPicker}>
                  <CompactPicker
                    id="lineColor"
                    color={lineColor}
                    onChange={color => {
                      setLineColor(color.hex);
                      setExpandColors(false);
                    }}
                  />
                </Collapse>
                <Tooltip title={'Erase ctrl+shift+e'} placement="left">
                  <IconButton color="secondary" onClick={clear}>
                    <img
                      src={require('../../assests/images/delete.png')}
                      className={classes.toolbarButton}
                      alt="EraseIcon"
                    ></img>
                  </IconButton>
                </Tooltip>
              </div>
            ) : null}
          </>
        </>
      ) : (
        <></>
      )}
    </div>
  );
}
